/**
 * OrdersContent - Component for managing orders
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Bell, PlusCircle, ShoppingCart, FileText, Clock, User, Phone,
  Truck, Store, Home, Wallet, CreditCard, CheckCircle, Package,
  DollarSign, MessageSquare, Printer, Bot, Calendar, AlertCircle, Bike,
  XCircle, Trash2, Zap, Settings, MessageCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import KanbanBoard from '../KanbanBoard';
import PDVScreen from '../../PDVScreen';
import { useWhatsAppIntegration, generateWhatsAppLink } from '../../hooks/useWhatsAppIntegration';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Função helper para garantir valores numéricos seguros
const safeNumber = (value, defaultValue = 0) => {
  const num = Number(value);
  return isNaN(num) ? defaultValue : num;
};

// Função helper para formatar preço com segurança
const formatPrice = (value) => {
  return safeNumber(value).toFixed(2);
};

// Função para sanitizar dados do pedido
const sanitizeOrder = (order) => {
  if (!order) return null;
  return {
    ...order,
    total: safeNumber(order.total),
    subtotal: safeNumber(order.subtotal),
    delivery_fee: safeNumber(order.delivery_fee),
    discount: safeNumber(order.discount),
    change_for: safeNumber(order.change_for),
    items: (order.items || []).map(item => ({
      ...item,
      price: safeNumber(item.price),
      quantity: safeNumber(item.quantity, 1),
      unit_price: safeNumber(item.unit_price || item.price),
      subtotal: safeNumber(item.subtotal || (safeNumber(item.price) * safeNumber(item.quantity, 1)))
    }))
  };
};

// Error Boundary Component
class OrdersErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('OrdersContent Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
          <AlertCircle className="w-16 h-16 text-red-400 mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Erro ao carregar pedidos</h2>
          <p className="text-gray-400 mb-4">Ocorreu um erro ao exibir os pedidos. Tente recarregar a página.</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            Recarregar Página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

const OrdersContent = ({ isPDVOpen, setIsPDVOpen }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [deliveryTypeFilter, setDeliveryTypeFilter] = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [loadError, setLoadError] = useState(null);
  
  // Entregadores
  const [deliveryDrivers, setDeliveryDrivers] = useState([]);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [pendingDeliveryOrder, setPendingDeliveryOrder] = useState(null);
  const [notifyingDriver, setNotifyingDriver] = useState(false);
  
  // Modal de Rejeição
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingOrder, setRejectingOrder] = useState(null);
  const [selectedRejectReason, setSelectedRejectReason] = useState('');
  const [customRejectMessage, setCustomRejectMessage] = useState('');
  const [isRejecting, setIsRejecting] = useState(false);
  
  // Modal de Exclusão
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingOrder, setDeletingOrder] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // WhatsApp API Integration Hook
  const whatsappIntegration = useWhatsAppIntegration();
  
  // WhatsApp connection status (legacy - mantido para compatibilidade)
  const [whatsappConnected, setWhatsappConnected] = useState(false);
  
  // Estados para notificação de pedidos pendentes
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioRef = React.useRef(null);
  
  // Estados para Auto-Accept
  const [autoAcceptEnabled, setAutoAcceptEnabled] = useState(false);
  const [autoAcceptLoading, setAutoAcceptLoading] = useState(false);
  const [autoAcceptMessage, setAutoAcceptMessage] = useState('Seu pedido foi recebido e já está em produção! 🍕');
  const [autoAcceptSendWhatsApp, setAutoAcceptSendWhatsApp] = useState(true);
  const [showAutoAcceptSettings, setShowAutoAcceptSettings] = useState(false);
  const [savingAutoAcceptSettings, setSavingAutoAcceptSettings] = useState(false);

  // Motivos de rejeição predefinidos
  const REJECT_REASONS = [
    { id: 'sem_motoboy', icon: '🛵', label: 'Sem motoboy', message: 'Nossos entregadores estão indisponíveis no momento.' },
    { id: 'produto_esgotado', icon: '📦', label: 'Produto em falta', message: 'Infelizmente um ou mais produtos do seu pedido estão esgotados.' },
    { id: 'restaurante_fechando', icon: '🏪', label: 'Restaurante fechando', message: 'Estamos encerrando nosso expediente.' },
    { id: 'fora_area', icon: '📍', label: 'Fora da área', message: 'Desculpe, mas não conseguimos realizar entregas na sua região.' },
    { id: 'restaurante_lotado', icon: '🔥', label: 'Muitos pedidos', message: 'Estamos com alta demanda e não conseguimos atender novos pedidos no momento.' },
    { id: 'horario_fechado', icon: '⏰', label: 'Horário encerrado', message: 'Nosso horário de funcionamento encerrou.' },
    { id: 'problema_pagamento', icon: '💳', label: 'Problema no pagamento', message: 'Houve um problema com a forma de pagamento informada.' },
    { id: 'outro', icon: '✏️', label: 'Outro motivo', message: '' },
  ];

  // Função para tocar som de notificação personalizado
  const playBuzzerSound = () => {
    if (!soundEnabled) return;
    
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio('/sounds/notification.mp3');
        audioRef.current.volume = 0.7;
      }
      
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(err => {
        console.log('Erro ao tocar som:', err);
      });
      
    } catch (err) {
      console.log('Erro ao tocar som de notificação:', err);
    }
  };

  // Função para verificar pedidos pendentes e tocar buzina
  const checkPendingOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sanitizar dados
      const allOrders = (response.data || []).map(sanitizeOrder).filter(Boolean);
      
      const pendingOrders = allOrders.filter(order => order.status === 'pendente');
      
      if (pendingOrders.length > 0 && soundEnabled) {
        playBuzzerSound();
      }
      
      if (!statusFilter && !deliveryTypeFilter) {
        setOrders(allOrders);
      }
      
    } catch (error) {
      console.error('Error checking pending orders:', error);
    }
  };

  useEffect(() => {
    fetchOrders();
    fetchDeliveryDrivers();
    checkPendingOrders();
    
    const intervalId = setInterval(() => {
      checkPendingOrders();
    }, 20000);
    
    return () => {
      clearInterval(intervalId);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [soundEnabled]);
  
  useEffect(() => {
    fetchOrders();
  }, [statusFilter, deliveryTypeFilter]);

  // Buscar configurações de auto-accept
  useEffect(() => {
    fetchAutoAcceptSetting();
  }, []);

  // Buscar entregadores ativos
  const fetchDeliveryDrivers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/delivery-drivers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filtrar apenas ativos
      setDeliveryDrivers((response.data || []).filter(d => d.active));
    } catch (error) {
      console.error('Error fetching delivery drivers:', error);
    }
  };

  // Notificar entregador via WhatsApp
  const notifyDeliveryDriver = async (driverId, orderId) => {
    try {
      setNotifyingDriver(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/delivery-drivers/${driverId}/notify?order_id=${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        toast.success('🛵 Entregador notificado com sucesso!');
      } else {
        // Mostrar mensagem de erro específica
        const errorMsg = response.data.error || 'Erro ao notificar entregador';
        if (errorMsg.includes('WhatsApp não configurado')) {
          toast.error('⚠️ WhatsApp não está configurado para este restaurante. Configure nas Configurações.');
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Error notifying driver:', error);
      const errorDetail = error.response?.data?.detail || error.response?.data?.error || 'Erro ao enviar notificação';
      toast.error(`Erro: ${errorDetail}`);
    } finally {
      setNotifyingDriver(false);
      setShowDriverModal(false);
      setPendingDeliveryOrder(null);
    }
  };

  // Selecionar entregador e notificar
  const handleSelectDriver = (driver) => {
    if (pendingDeliveryOrder) {
      notifyDeliveryDriver(driver.id, pendingDeliveryOrder.id);
    }
  };
  
  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
  };

  // ============= AUTO-ACCEPT FUNCTIONS =============
  const fetchAutoAcceptSetting = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/restaurant/auto-accept-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoAcceptEnabled(response.data.auto_accept_orders || false);
      setAutoAcceptMessage(response.data.auto_accept_message || 'Seu pedido foi recebido e já está em produção! 🍕');
      setAutoAcceptSendWhatsApp(response.data.auto_accept_send_whatsapp !== false);
    } catch (error) {
      console.error('Error fetching auto-accept setting:', error);
    }
  };
  
  const toggleAutoAccept = async () => {
    try {
      setAutoAcceptLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/restaurant/auto-accept-orders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAutoAcceptEnabled(response.data.auto_accept_orders);
      toast.success(
        response.data.auto_accept_orders 
          ? '⚡ Auto-aceitar pedidos ativado!' 
          : '⚡ Auto-aceitar pedidos desativado'
      );
    } catch (error) {
      console.error('Error toggling auto-accept:', error);
      toast.error('Erro ao alterar configuração');
    } finally {
      setAutoAcceptLoading(false);
    }
  };
  
  const saveAutoAcceptSettings = async () => {
    try {
      setSavingAutoAcceptSettings(true);
      const token = localStorage.getItem('token');
      await axios.put(`${API}/restaurant/auto-accept-orders`, {
        auto_accept_message: autoAcceptMessage,
        auto_accept_send_whatsapp: autoAcceptSendWhatsApp
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('✅ Configurações salvas!');
      setShowAutoAcceptSettings(false);
    } catch (error) {
      console.error('Error saving auto-accept settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSavingAutoAcceptSettings(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadError(null);
      const token = localStorage.getItem('token');
      let params = '';
      const filters = [];
      if (statusFilter) filters.push(`status=${statusFilter}`);
      if (deliveryTypeFilter) filters.push(`delivery_type=${deliveryTypeFilter}`);
      if (filters.length > 0) params = '?' + filters.join('&');
      
      const response = await axios.get(`${API}/orders${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Sanitizar todos os pedidos para evitar erros de renderização
      const sanitizedOrders = (response.data || []).map(sanitizeOrder).filter(Boolean);
      
      // Ordenar por data de criação (mais recente primeiro)
      // Garantir ordenação correta mesmo com formatos de data diferentes
      sanitizedOrders.sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA; // Mais recente primeiro
      });
      
      setOrders(sanitizedOrders);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      setLoadError(error.message || 'Erro ao carregar pedidos');
      setLoading(false);
    }
  };

  const getFirstName = (fullName) => {
    if (!fullName) return 'Cliente';
    return fullName.trim().split(' ')[0];
  };
  
  const generateWhatsAppMessage = (order, newStatus) => {
    const firstName = getFirstName(order.customer_name);
    const isPickup = order.delivery_type === 'retirada' || order.delivery_type === 'pickup';
    
    const messages = {
      'em_producao': `🎉 Olá ${firstName}! Seu pedido está em produção e já estamos preparando com todo carinho! 😊`,
      'entrega': isPickup 
        ? `✅ ${firstName}, seu pedido está pronto para retirada! Estamos te esperando! 🏪`
        : `🚀 ${firstName}, seu pedido saiu para entrega! Logo logo está aí! 🛵`,
      'pronto': isPickup
        ? `🎉 ${firstName}, obrigado por retirar seu pedido! Volte sempre! 🤗`
        : `✅ ${firstName}, seu pedido foi entregue! Obrigado e volte sempre! 🤗`
    };
    
    return messages[newStatus] || `Olá ${firstName}! Status do seu pedido foi atualizado.`;
  };
  
  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const order = orders.find(o => o.id === orderId);
      
      if (!newStatus) {
        newStatus = getNextStatus(order.status);
      }
      
      if (!newStatus) return;
      
      // Verificar se está mudando para "entrega" e é delivery (não retirada)
      const isDelivery = order.delivery_type !== 'retirada' && order.delivery_type !== 'pickup';
      const isChangingToDelivery = newStatus === 'entrega' && isDelivery;
      
      // Se está mudando para "entrega" e há entregadores cadastrados
      if (isChangingToDelivery && deliveryDrivers.length > 0) {
        // Primeiro, atualiza o status
        await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Se tiver apenas 1 entregador, notifica automaticamente
        if (deliveryDrivers.length === 1) {
          notifyDeliveryDriver(deliveryDrivers[0].id, orderId);
        } else {
          // Se tiver mais de 1, mostra modal para selecionar
          setPendingDeliveryOrder(order);
          setShowDriverModal(true);
        }
      } else {
        // Fluxo normal sem notificação de entregador
        await axios.patch(`${API}/orders/${orderId}/status`, { status: newStatus }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      // Enviar mensagem para o cliente
      if (order && order.customer_phone) {
        const message = generateWhatsAppMessage(order, newStatus);
        const phoneNumber = order.customer_phone.replace(/\D/g, '');
        
        if (whatsappIntegration.isConnected) {
          try {
            const result = await whatsappIntegration.sendMessage(phoneNumber, message);
            if (result.success) {
              toast.success('✅ Mensagem enviada via WhatsApp API!');
            } else {
              toast.error('Erro ao enviar via API. Tente manualmente.');
            }
          } catch (error) {
            console.error('Failed to send via API:', error);
            toast.error('Erro ao enviar mensagem via API');
          }
        } else {
          const whatsappUrl = generateWhatsAppLink(phoneNumber, message);
          window.open(whatsappUrl, '_blank');
          toast.info('📱 Abrindo WhatsApp manual (API não conectada)');
        }
      }
      
      fetchOrders();
    } catch (error) {
      console.error('Error updating order status:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return 'bg-yellow-500 text-white';
      case 'em_producao': return 'bg-emerald-500 text-white';
      case 'entrega': return 'bg-purple-500 text-white';
      case 'pronto': return 'bg-green-500 text-white';
      case 'cancelado': return 'bg-red-500 text-white';
      case 'rejeitado': return 'bg-red-600 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Retorna o label do status baseado no tipo de entrega
  const getStatusLabel = (status, deliveryType = 'delivery') => {
    // Para status 'entrega', diferenciar entre delivery e retirada
    if (status === 'entrega') {
      if (deliveryType === 'retirada' || deliveryType === 'pickup') {
        return 'Pronto p/ Retirada';
      }
      return 'Saiu p/ Entrega';
    }
    
    // Para status 'pronto', diferenciar entre delivery e retirada
    if (status === 'pronto') {
      if (deliveryType === 'retirada' || deliveryType === 'pickup') {
        return 'Retirado';
      }
      return 'Entregue';
    }
    
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'em_producao': return 'Em Produção';
      case 'cancelado': return 'Cancelado';
      case 'rejeitado': return 'Rejeitado';
      default: return status;
    }
  };

  // Abrir modal de rejeição
  const openRejectModal = (order) => {
    setShowOrderDetails(false);  // Fechar o Dialog principal primeiro
    setRejectingOrder(order);
    setSelectedRejectReason('');
    setCustomRejectMessage('');
    setTimeout(() => {
      setShowRejectModal(true);
    }, 100);  // Pequeno delay para o Dialog fechar antes
  };

  // Confirmar rejeição do pedido
  const confirmRejectOrder = async () => {
    if (!rejectingOrder) return;
    if (!selectedRejectReason) {
      toast.error('Selecione um motivo para rejeitar o pedido');
      return;
    }
    
    setIsRejecting(true);
    try {
      const token = localStorage.getItem('token');
      const reasonData = REJECT_REASONS.find(r => r.id === selectedRejectReason);
      const customMessage = selectedRejectReason === 'outro' ? customRejectMessage : reasonData?.message;
      
      await axios.post(`${API}/orders/${rejectingOrder.id}/reject`, { 
        reason: selectedRejectReason,
        custom_message: customMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('❌ Pedido rejeitado com sucesso');
      setShowRejectModal(false);
      setShowOrderDetails(false);
      setSelectedOrder(null);
      setRejectingOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Error rejecting order:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao rejeitar pedido';
      toast.error(errorMsg);
    } finally {
      setIsRejecting(false);
    }
  };

  // Abrir modal de exclusão
  const openDeleteModal = (order) => {
    setShowOrderDetails(false);  // Fechar o Dialog principal primeiro
    setDeletingOrder(order);
    setTimeout(() => {
      setShowDeleteModal(true);
    }, 100);  // Pequeno delay para o Dialog fechar antes
  };

  // Confirmar exclusão do pedido
  const confirmDeleteOrder = async () => {
    if (!deletingOrder) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/orders/${deletingOrder.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('🗑️ Pedido excluído com sucesso');
      setShowDeleteModal(false);
      setShowOrderDetails(false);
      setSelectedOrder(null);
      setDeletingOrder(null);
      fetchOrders();
    } catch (error) {
      console.error('Error deleting order:', error);
      const errorMsg = error.response?.data?.detail || 'Erro ao excluir pedido';
      toast.error(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = {
      'pendente': 'em_producao',
      'em_producao': 'entrega',
      'entrega': 'pronto'
    };
    return statusFlow[currentStatus];
  };

  // Retorna o label do próximo status baseado no tipo de entrega
  const getNextStatusLabel = (currentStatus, deliveryType = 'delivery') => {
    const nextStatus = getNextStatus(currentStatus);
    return nextStatus ? getStatusLabel(nextStatus, deliveryType) : null;
  };

  const printOrder = (order) => {
    // Debug: ver dados do pedido
    console.log('🖨️ Imprimindo pedido:', {
      id: order.id,
      delivery_fee: order.delivery_fee,
      delivery_zone_name: order.delivery_zone_name,
      subtotal: order.subtotal,
      total: order.total,
      address: order.address
    });
    
    const printWindow = window.open('', '_blank');
    const address = order.address || {};
    // Suportar address como string (pedidos WhatsApp) ou objeto (pedidos web)
    const addressStr = typeof address === 'string' 
      ? address 
      : [address.street, address.number, address.neighborhood, address.city].filter(Boolean).join(', ');
    
    // Obter bairro de qualquer fonte disponível
    const bairroName = order.delivery_zone_name || (typeof address === 'object' ? address.neighborhood : '') || '';
    
    // Calcular subtotal dos itens se não existir
    const calcularSubtotalItens = () => {
      if (!order.items || order.items.length === 0) return 0;
      return order.items.reduce((sum, item) => {
        const itemPrice = Number(item.price || 0) * Number(item.quantity || 1);
        const additionalsPrice = (item.additionals || []).reduce((addSum, add) => {
          return addSum + (Number(add.price || 0) * Number(add.quantity || 1));
        }, 0);
        return sum + itemPrice + additionalsPrice;
      }, 0);
    };
    
    // Obter taxa de entrega - calcular se não existir
    let taxaEntrega = null;
    if (order.delivery_fee !== undefined && order.delivery_fee !== null && order.delivery_fee > 0) {
      // Taxa está salva no pedido
      taxaEntrega = Number(order.delivery_fee);
    } else if (order.delivery_type === 'delivery' && order.total) {
      // Calcular taxa: Total - Subtotal dos itens - Desconto
      const subtotalItens = order.subtotal || calcularSubtotalItens();
      const desconto = Number(order.discount || 0) + Number(order.cashback_used || 0) + Number(order.coupon_discount || 0);
      const taxaCalculada = Number(order.total) - subtotalItens + desconto;
      // Só mostrar se a taxa calculada for positiva e fizer sentido
      if (taxaCalculada > 0 && taxaCalculada < 100) { // Limite razoável para taxa
        taxaEntrega = taxaCalculada;
      }
    }
    
    console.log('📊 Taxa calculada:', taxaEntrega);
    
    const formatAdditionals = (item) => {
      if (!item.additionals || item.additionals.length === 0) return '';
      return item.additionals.map(add => `
        <div style="padding-left: 20px; color: #666; font-size: 12px;">
          + ${add.name || add.additional_name} ${add.price ? `- R$ ${Number(add.price).toFixed(2)}` : ''}
        </div>
      `).join('');
    };

    const paymentLabels = {
      'pix': 'PIX',
      'dinheiro': 'Dinheiro',
      'cartao': 'Cartão',
      'cartao_credito': 'Cartão de Crédito',
      'cartao_debito': 'Cartão de Débito',
      'vale_refeicao': 'Vale Refeição',
      'vale_alimentacao': 'Vale Alimentação'
    };
    const paymentMethod = paymentLabels[order.payment_method] || order.payment_method || 'Não informado';
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Pedido #${order.order_number}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; max-width: 80mm; margin: 0 auto; }
            h1 { font-size: 20px; margin-bottom: 10px; text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .info { margin: 8px 0; font-size: 14px; }
            .info strong { display: inline-block; min-width: 80px; }
            .section-title { font-size: 16px; font-weight: bold; margin-top: 15px; margin-bottom: 10px; border-bottom: 1px dashed #ccc; padding-bottom: 5px; }
            .items { margin-top: 15px; }
            .item { padding: 8px 0; border-bottom: 1px solid #eee; }
            .item-name { font-weight: 500; }
            .item-price { float: right; }
            .item-obs { padding-left: 20px; color: #666; font-size: 12px; font-style: italic; margin-top: 4px; }
            .total-section { margin-top: 20px; border-top: 2px solid #000; padding-top: 10px; }
            .total { font-size: 18px; font-weight: bold; }
            .payment { margin-top: 10px; padding: 10px; background: #f5f5f5; border-radius: 4px; }
            .footer { margin-top: 20px; text-align: center; font-size: 12px; color: #666; border-top: 1px dashed #ccc; padding-top: 10px; }
          </style>
        </head>
        <body>
          <h1>PEDIDO #${order.order_number}</h1>
          
          ${order.is_scheduled && order.scheduled_date && order.scheduled_time ? `
            <div style="background: #e0f2fe; border: 2px solid #0ea5e9; padding: 10px; margin-bottom: 15px; border-radius: 8px; text-align: center;">
              <div style="font-size: 12px; font-weight: bold; color: #0369a1;">📅 PEDIDO AGENDADO</div>
              <div style="font-size: 18px; font-weight: bold; color: #0c4a6e;">${order.scheduled_date} às ${order.scheduled_time}</div>
            </div>
          ` : ''}
          
          <div class="info"><strong>Cliente:</strong> ${order.customer_name || 'Cliente'}</div>
          <div class="info"><strong>Data/Hora:</strong> ${new Date(order.created_at).toLocaleString('pt-BR')}</div>
          ${order.customer_phone ? `<div class="info"><strong>Telefone:</strong> ${order.customer_phone}</div>` : ''}
          
          ${order.delivery_type ? `<div class="info"><strong>Tipo:</strong> ${order.delivery_type === 'delivery' ? '🛵 Delivery' : order.delivery_type === 'mesa' ? '🍽️ Mesa' : '🏃 Retirada'}</div>` : ''}
          ${order.table_number ? `<div class="info"><strong>Mesa:</strong> ${order.table_number}</div>` : ''}
          
          ${addressStr ? `
            <div class="section-title">📍 Endereço de Entrega</div>
            <div class="info">${addressStr}</div>
            ${address.complement ? `<div class="info">Complemento: ${address.complement}</div>` : ''}
            ${address.reference ? `<div class="info">Referência: ${address.reference}</div>` : ''}
            ${bairroName ? `<div class="info"><strong>Bairro:</strong> ${bairroName}</div>` : ''}
            ${taxaEntrega !== null && taxaEntrega !== undefined ? `<div class="info"><strong>🚚 Taxa de Entrega:</strong> R$ ${Number(taxaEntrega).toFixed(2)}</div>` : ''}
          ` : ''}
          
          <div class="section-title">📝 Itens do Pedido</div>
          <div class="items">
            ${order.items?.map(item => `
              <div class="item">
                <div>
                  <span class="item-name">${item.quantity}x ${item.product_name || item.name}</span>
                  <span class="item-price">R$ ${(Number(item.price || 0) * Number(item.quantity || 1)).toFixed(2)}</span>
                </div>
                ${formatAdditionals(item)}
                ${item.observation || item.notes ? `<div class="item-obs">📌 Obs: ${item.observation || item.notes}</div>` : ''}
              </div>
            `).join('') || '<div class="item">Nenhum item</div>'}
          </div>
          
          ${order.notes ? `
            <div class="section-title">📋 Observações do Pedido</div>
            <div class="info">${order.notes}</div>
          ` : ''}
          
          <div class="total-section">
            ${order.subtotal ? `<div class="info"><strong>Subtotal:</strong> R$ ${Number(order.subtotal).toFixed(2)}</div>` : ''}
            ${taxaEntrega && taxaEntrega > 0 ? `<div class="info"><strong>Taxa de Entrega:</strong> R$ ${Number(taxaEntrega).toFixed(2)}</div>` : ''}
            ${order.coupon_code && order.coupon_discount > 0 ? `<div class="info" style="color: #10b981;"><strong>Cupom (${order.coupon_code}):</strong> -R$ ${Number(order.coupon_discount).toFixed(2)}</div>` : ''}
            ${!order.coupon_code && order.discount > 0 ? `<div class="info" style="color: #10b981;"><strong>Desconto:</strong> -R$ ${Number(order.discount).toFixed(2)}</div>` : ''}
            ${order.cashback_used > 0 ? `<div class="info" style="color: #f59e0b;"><strong>Cashback:</strong> -R$ ${Number(order.cashback_used).toFixed(2)}</div>` : ''}
            <div class="total">TOTAL: R$ ${Number(order.total || 0).toFixed(2)}</div>
            <div class="payment">
              <strong>💳 Forma de Pagamento:</strong> ${paymentMethod}
              ${order.payment_method === 'dinheiro' && order.change_for ? `<br><strong>Troco para:</strong> R$ ${Number(order.change_for).toFixed(2)}` : ''}
            </div>
          </div>
          
          <div class="footer">
            <p>Obrigado pela preferência!</p>
            <p>${new Date().toLocaleString('pt-BR')}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatAddress = (address) => {
    if (!address) return '';
    // Suportar address como string (pedidos WhatsApp) ou objeto (pedidos web)
    if (typeof address === 'string') return address;
    if (typeof address !== 'object') return '';
    const parts = [
      address.street,
      address.number,
      address.neighborhood,
      address.city,
      address.state
    ].filter(Boolean);
    return parts.join(', ');
  };

  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Mostrar erro se houver problema no carregamento
  if (loadError) {
    return (
      <div className="glass-card p-8 text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Erro ao carregar pedidos</h2>
        <p className="text-gray-400 mb-4">{loadError}</p>
        <button
          onClick={() => {
            setLoadError(null);
            setLoading(true);
            fetchOrders();
          }}
          className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-3xl font-bold text-white">Pedidos</h1>
        <div className="flex items-center gap-3">
          {/* Toggle Auto-Accept */}
          <div className="flex items-center gap-1">
            <div 
              className="flex items-center gap-2 cursor-pointer group"
              onClick={toggleAutoAccept}
              title={autoAcceptEnabled ? 'Auto-aceitar pedidos ATIVO' : 'Auto-aceitar pedidos desativado'}
              data-testid="auto-accept-toggle"
            >
              <span className={`text-xs font-medium transition-colors ${autoAcceptEnabled ? 'text-emerald-400' : 'text-gray-500'}`}>
                Auto
              </span>
              <div className={`relative w-9 h-5 rounded-full transition-colors ${autoAcceptEnabled ? 'bg-emerald-500/30' : 'bg-gray-700'} ${autoAcceptLoading ? 'opacity-50' : ''}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full transition-all ${
                  autoAcceptEnabled 
                    ? 'left-[18px] bg-emerald-400' 
                    : 'left-0.5 bg-gray-500'
                }`}>
                  {autoAcceptLoading ? (
                    <div className="w-full h-full rounded-full border-2 border-transparent border-t-white animate-spin" />
                  ) : (
                    <Zap size={10} className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ${autoAcceptEnabled ? 'text-emerald-900' : 'text-gray-700'}`} />
                  )}
                </div>
              </div>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setShowAutoAcceptSettings(true); }}
              className={`p-1.5 rounded transition-all ${autoAcceptEnabled ? 'text-emerald-400 hover:bg-emerald-500/20' : 'text-gray-500 hover:bg-gray-700'}`}
              title="Configurar mensagem automática"
            >
              <Settings size={14} />
            </button>
          </div>
          
          <button
            onClick={toggleSound}
            className={`p-3 rounded-lg transition-all ${
              soundEnabled 
                ? 'bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 border border-orange-500/30' 
                : 'bg-gray-700 text-gray-400 hover:bg-gray-600 border border-gray-600'
            }`}
            title={soundEnabled ? 'Notificação sonora ativada' : 'Notificação sonora desativada'}
            data-testid="sound-toggle-button"
          >
            <Bell size={18} className={soundEnabled ? 'animate-pulse' : ''} />
          </button>
          
          <button 
            onClick={() => setIsPDVOpen(true)}
            className="glass-button-primary"
            data-testid="open-pdv-button"
          >
            <PlusCircle size={18} className="mr-2" />
            Novo Pedido
          </button>
        </div>
      </div>

      {/* Modal de Configurações Auto-Accept */}
      <Dialog open={showAutoAcceptSettings} onOpenChange={setShowAutoAcceptSettings}>
        <DialogContent className="bg-gray-900 border border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <Zap className="text-emerald-400" size={20} />
              Configurar Auto-Aceitar
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-5 pt-2">
            <div className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg border border-gray-700">
              <div className="flex items-center gap-3">
                <MessageCircle className="text-green-400" size={20} />
                <div>
                  <p className="text-white text-sm font-medium">Enviar WhatsApp</p>
                  <p className="text-gray-400 text-xs">Notificar cliente quando pedido for aceito</p>
                </div>
              </div>
              <button
                onClick={() => setAutoAcceptSendWhatsApp(!autoAcceptSendWhatsApp)}
                className={`relative w-11 h-6 rounded-full transition-colors ${autoAcceptSendWhatsApp ? 'bg-green-500' : 'bg-gray-600'}`}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${autoAcceptSendWhatsApp ? 'left-6' : 'left-1'}`} />
              </button>
            </div>
            
            <div className={`space-y-2 ${!autoAcceptSendWhatsApp ? 'opacity-50 pointer-events-none' : ''}`}>
              <label className="block text-sm text-gray-300">Mensagem de confirmação</label>
              <textarea
                value={autoAcceptMessage}
                onChange={(e) => setAutoAcceptMessage(e.target.value)}
                className="w-full bg-gray-800 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm resize-none focus:border-emerald-500 focus:outline-none"
                rows={3}
                placeholder="Seu pedido foi recebido e já está em produção! 🍕"
              />
              <p className="text-xs text-gray-500">
                Use <span className="text-emerald-400">{'{nome}'}</span> para nome do cliente, <span className="text-emerald-400">{'{pedido}'}</span> para número do pedido
              </p>
            </div>
            
            {autoAcceptSendWhatsApp && (
              <div className="p-3 bg-gray-800/30 rounded-lg border border-gray-700/50">
                <p className="text-xs text-gray-500 mb-2">Preview da mensagem:</p>
                <p className="text-sm text-gray-300 italic">
                  "{autoAcceptMessage.replace('{nome}', 'João').replace('{pedido}', '#WEB123').replace('{loja}', 'Sua Loja')}"
                </p>
              </div>
            )}
            
            <div className="flex gap-3 pt-2">
              <button
                onClick={() => setShowAutoAcceptSettings(false)}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={saveAutoAcceptSettings}
                disabled={savingAutoAcceptSettings}
                className="flex-1 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-600 text-white rounded-lg transition-colors text-sm flex items-center justify-center gap-2"
              >
                {savingAutoAcceptSettings ? 'Salvando...' : 'Salvar'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters */}
      <div className="glass-card">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setDeliveryTypeFilter(''); setStatusFilter(''); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !deliveryTypeFilter && !statusFilter
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setDeliveryTypeFilter('delivery')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              deliveryTypeFilter === 'delivery'
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Delivery
          </button>
          <button
            onClick={() => setDeliveryTypeFilter('mesa')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              deliveryTypeFilter === 'mesa'
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Mesas
          </button>
          <button
            onClick={() => setDeliveryTypeFilter('retirada')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              deliveryTypeFilter === 'retirada'
                ? 'bg-red-500 text-white'
                : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
            }`}
          >
            Retirada
          </button>
        </div>
      </div>

      {/* Desktop Kanban View */}
      <div className="hidden lg:block" data-testid="desktop-kanban-view">
        <KanbanBoard
          orders={orders}
          onPrint={printOrder}
          onUpdateStatus={updateOrderStatus}
          setOrders={setOrders}
          onCardClick={(order) => { setSelectedOrder(order); setShowOrderDetails(true); }}
        />
      </div>

      {/* Mobile List View */}
      <div className="lg:hidden space-y-4" data-testid="orders-list">
        {orders.map((order) => {
          const address = order.address || {};
          const addressStr = formatAddress(address);
          const orderDate = new Date(order.created_at);
          const timeStr = orderDate.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
          const dateStr = orderDate.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
          
          return (
            <div 
              key={order.id} 
              className="glass-card hover:bg-white/15 transition-all cursor-pointer"
              onClick={() => { setSelectedOrder(order); setShowOrderDetails(true); }}
              data-testid={`order-card-${order.id}`}
            >
              {/* Order Header */}
              <div className="pb-4 border-b border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-bold text-white text-lg">
                        {order.customer_name || 'Cliente'}
                      </h3>
                      {order.source === 'whatsapp' && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 border border-green-500/30 rounded-md text-green-400 text-xs font-medium">
                          <Bot className="w-3 h-3" />
                          IA
                        </span>
                      )}
                      {order.is_scheduled && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 border border-emerald-500/30 rounded-md text-emerald-400 text-xs font-medium">
                          <Calendar className="w-3 h-3" />
                          Agendado
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-300 mt-1">Pedido #{order.order_number}</p>
                    {order.is_scheduled && order.scheduled_date && order.scheduled_time && (
                      <p className="text-xs text-emerald-400 mt-0.5 font-medium">
                        📅 {order.scheduled_date} às {order.scheduled_time}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-0.5">Em {dateStr} às {timeStr}</p>
                  </div>
                  <div className="flex flex-col gap-1 items-end">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status, order.delivery_type)}
                    </span>
                    {order.payment_method === 'pix' && (order.payment_status === 'paid' || order.status_pagamento === 'approved') && (
                      <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-green-500/20 text-green-400 border border-green-500/40 flex items-center gap-0.5">
                        <span className="text-[10px]">✓</span> pago
                      </span>
                    )}
                  </div>
                </div>
                
                {addressStr && (
                  <div className="mt-3 text-sm text-gray-300">
                    <p className="line-clamp-2">📍 {addressStr}</p>
                  </div>
                )}
                
                {order.table_number && (
                  <div className="mt-3 text-sm text-gray-300">
                    <p>🪑 Mesa {order.table_number}</p>
                  </div>
                )}
                
                {order.items && order.items.length > 0 && (
                  <div className="mt-3 space-y-1">
                    {order.items.slice(0, 3).map((item, idx) => (
                      <div key={idx} className="text-sm text-gray-300">
                        {item.quantity}x {item.product_name || item.name || item.additional_name || 'Item'}
                      </div>
                    ))}
                    {order.items.length > 3 && (
                      <p className="text-xs text-gray-400 italic">
                        + {order.items.length - 3} item(ns) adicional(is)
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Order Footer */}
              <div className="pt-4">
                {/* Delivery info - Bairro e Taxa */}
                {order.delivery_type === 'delivery' && (order.delivery_fee > 0 || order.delivery_zone_name) && (
                  <div className="mb-3 pb-3 border-b border-white/10">
                    <div className="flex items-center justify-between text-sm">
                      {order.delivery_zone_name && (
                        <span className="text-gray-400">
                          🏘️ {order.delivery_zone_name}
                        </span>
                      )}
                      {order.delivery_fee > 0 && (
                        <span className="text-orange-400 font-medium">
                          Taxa: R$ {(order.delivery_fee || 0).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xl font-bold text-white">
                      R$ {order.total?.toFixed(2) || '0.00'}
                    </span>
                    {order.payment_method === 'pix' && (order.payment_status === 'paid' || order.status_pagamento === 'approved') && (
                      <span className="px-2 py-1 rounded-md text-xs font-semibold bg-green-500/20 text-green-400 border border-green-500/50 flex items-center gap-1">
                        ✓ PIX Pago
                      </span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        printOrder(order);
                      }}
                      className="px-3 py-2 border border-white/20 text-gray-300 rounded-lg text-sm font-medium hover:bg-white/10 hover:text-white transition-colors flex items-center gap-1"
                      data-testid={`print-order-${order.id}`}
                    >
                      <FileText size={16} />
                      Imprimir
                    </button>
                    
                    {getNextStatus(order.status) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          updateOrderStatus(order.id, getNextStatus(order.status));
                        }}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium hover:bg-emerald-600 transition-colors"
                        data-testid={`update-status-${order.id}`}
                      >
                        {getNextStatusLabel(order.status, order.delivery_type)} →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {orders.length === 0 && (
        <div className="glass-card text-center py-12">
          <ShoppingCart size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            Nenhum pedido encontrado
          </h3>
          <p className="text-gray-400 mb-6">
            {deliveryTypeFilter 
              ? `Não há pedidos do tipo "${deliveryTypeFilter}"`
              : 'Crie seu primeiro pedido para começar'
            }
          </p>
          <button 
            onClick={() => setIsPDVOpen(true)}
            className="glass-button-primary"
          >
            Criar Primeiro Pedido
          </button>
        </div>
      )}

      {/* Order Details Modal */}
      <Dialog open={showOrderDetails} onOpenChange={setShowOrderDetails}>
        <DialogContent className="glass-modal max-w-4xl max-h-[90vh] overflow-y-auto p-0 sm:p-6 w-[95vw] sm:w-full">
          {/* Mobile-optimized header */}
          <DialogHeader className="sticky top-0 z-10 bg-gray-900/95 backdrop-blur-sm p-3 sm:p-4 pr-12 sm:pr-14 border-b border-gray-700 sm:border-0 sm:bg-transparent">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="text-white text-lg sm:text-2xl font-bold flex items-center gap-2">
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg sm:rounded-xl flex items-center justify-center shadow-lg">
                  <ShoppingCart className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                </div>
                <span className="text-base sm:text-2xl">#{selectedOrder?.order_number}</span>
              </DialogTitle>
              <span className={`px-2 py-1 sm:px-4 sm:py-2 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold shadow-lg ${getStatusColor(selectedOrder?.status)}`}>
                {getStatusLabel(selectedOrder?.status, selectedOrder?.delivery_type)}
              </span>
            </div>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="p-3 sm:p-0 space-y-3 sm:space-y-4">
              {/* Data e Cliente - Layout compacto em mobile */}
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-2 sm:gap-4">
                {/* Data */}
                <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-2 sm:p-4 sm:bg-gradient-to-r sm:from-emerald-500/10 sm:to-cyan-500/10">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide font-semibold hidden sm:block">Data do Pedido</p>
                      <p className="text-white font-semibold text-xs sm:text-lg truncate">
                        {new Date(selectedOrder.created_at).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Cliente - Compacto */}
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <User className="w-4 h-4 sm:w-5 sm:h-5 text-purple-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] sm:text-xs text-gray-400 uppercase tracking-wide font-semibold hidden sm:block">Cliente</p>
                      <div className="flex items-center gap-1">
                        <p className="text-white font-bold text-xs sm:text-lg truncate">{selectedOrder.customer_name || 'Cliente'}</p>
                        {selectedOrder.source === 'whatsapp' && (
                          <span className="inline-flex items-center px-1 py-0.5 bg-green-500/20 rounded text-green-400 text-[9px] sm:text-xs font-medium">
                            <Bot className="w-2 h-2 sm:w-3 sm:h-3" />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheduled Order Info - Compacto */}
              {selectedOrder.is_scheduled && selectedOrder.scheduled_date && selectedOrder.scheduled_time && (
                <div className="bg-indigo-500/20 border border-indigo-500/40 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Calendar className="w-4 h-4 sm:w-6 sm:h-6 text-indigo-400" />
                    <div>
                      <span className="text-indigo-300 text-[10px] sm:text-xs font-bold uppercase">📅 Agendado</span>
                      <p className="text-white font-bold text-sm sm:text-xl">
                        {selectedOrder.scheduled_date} às {selectedOrder.scheduled_time}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Telefone - Clicável para WhatsApp */}
              {selectedOrder.customer_phone && (
                <a 
                  href={`https://wa.me/${selectedOrder.customer_phone.replace(/\D/g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 bg-green-500/20 border border-green-500/30 rounded-lg p-2 sm:p-3 hover:bg-green-500/30 transition-colors"
                >
                  <Phone className="w-4 h-4 text-green-400" />
                  <span className="text-green-300 font-medium text-sm">{selectedOrder.customer_phone}</span>
                  <span className="ml-auto text-green-400 text-xs">Abrir WhatsApp →</span>
                </a>
              )}

              {/* Entrega e Pagamento - Grid compacto */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                {/* Entrega/Retirada */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    {selectedOrder.delivery_type === 'delivery' ? (
                      <Truck className="w-4 h-4 text-green-400" />
                    ) : selectedOrder.delivery_type === 'mesa' ? (
                      <Store className="w-4 h-4 text-emerald-400" />
                    ) : (
                      <Home className="w-4 h-4 text-orange-400" />
                    )}
                    <span className="text-[10px] sm:text-xs text-gray-400 uppercase font-semibold">
                      {selectedOrder.delivery_type === 'delivery' ? 'Entrega' : 
                       selectedOrder.delivery_type === 'mesa' ? 'Mesa' : 'Retirada'}
                    </span>
                  </div>
                  {selectedOrder.delivery_type === 'mesa' && selectedOrder.table_number && (
                    <p className="text-white font-bold text-sm sm:text-lg">Mesa {selectedOrder.table_number}</p>
                  )}
                  {selectedOrder.delivery_type === 'delivery' && formatAddress(selectedOrder.address) && (
                    <p className="text-gray-300 text-[11px] sm:text-sm leading-tight line-clamp-2">
                      {formatAddress(selectedOrder.address)}
                    </p>
                  )}
                </div>

                {/* Pagamento */}
                <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-2 sm:p-4">
                  <div className="flex items-center gap-2 mb-1 sm:mb-2">
                    {selectedOrder.payment_method === 'dinheiro' ? (
                      <Wallet className="w-4 h-4 text-yellow-400" />
                    ) : selectedOrder.payment_method === 'pix' ? (
                      <span className="text-yellow-400 font-bold text-sm">₿</span>
                    ) : (
                      <CreditCard className="w-4 h-4 text-yellow-400" />
                    )}
                    <span className="text-[10px] sm:text-xs text-gray-400 uppercase font-semibold">Pagamento</span>
                  </div>
                  <p className="text-white font-bold text-sm sm:text-lg capitalize">
                    {selectedOrder.payment_method === 'dinheiro' && '💵 Dinheiro'}
                    {selectedOrder.payment_method === 'pix' && '📱 PIX'}
                    {selectedOrder.payment_method === 'cartao' && '💳 Cartão'}
                    {!['dinheiro', 'pix', 'cartao'].includes(selectedOrder.payment_method) && 
                      (selectedOrder.payment_method || '-')}
                  </p>
                  {selectedOrder.payment_method === 'pix' && selectedOrder.payment_status === 'paid' && (
                    <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 bg-green-500 rounded text-white text-[10px] font-bold">
                      <CheckCircle className="w-3 h-3" /> PAGO
                    </span>
                  )}
                  {selectedOrder.payment_method === 'dinheiro' && selectedOrder.change_for && (
                    <p className="text-yellow-300 text-[10px] sm:text-xs mt-1">
                      Troco: R$ {Number(selectedOrder.change_for).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              {/* Items - Compacto */}
              <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-2 sm:p-4 overflow-hidden">
                <div className="flex items-center gap-2 mb-2 sm:mb-4">
                  <Package className="w-4 h-4 sm:w-5 sm:h-5 text-slate-300" />
                  <h3 className="text-sm sm:text-lg font-bold text-white">Itens</h3>
                  <span className="ml-auto text-gray-400 text-xs">{selectedOrder.items?.length || 0} itens</span>
                </div>
                <div className="space-y-2 sm:space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="bg-slate-900/50 border border-slate-700 rounded-lg p-2 sm:p-4 overflow-hidden">
                      <div className="flex justify-between items-start gap-2">
                        <div className="flex-1 min-w-0 overflow-hidden">
                          <div className="flex items-start gap-1 sm:gap-2">
                            <span className="bg-orange-500 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded flex-shrink-0">
                              {item.quantity}x
                            </span>
                            <p className="text-white font-semibold text-xs sm:text-base break-words line-clamp-2">
                              {item.product_name || item.name || item.additional_name || 'Item'}
                            </p>
                          </div>
                          {item.additionals && item.additionals.length > 0 && (
                            <div className="mt-1 pl-2 border-l-2 border-green-500/30 space-y-1">
                              {/* Agrupar adicionais por grupo */}
                              {(() => {
                                // Agrupar adicionais por group_name
                                const grouped = {};
                                const ungrouped = [];
                                
                                item.additionals.forEach(add => {
                                  const groupName = add.group_name || add.additional_group_name;
                                  if (groupName) {
                                    if (!grouped[groupName]) grouped[groupName] = [];
                                    grouped[groupName].push(add);
                                  } else {
                                    ungrouped.push(add);
                                  }
                                });
                                
                                return (
                                  <>
                                    {/* Adicionais agrupados */}
                                    {Object.entries(grouped).map(([groupName, addItems]) => (
                                      <div key={groupName}>
                                        <p className="text-[9px] sm:text-[10px] text-gray-400 uppercase font-semibold">{groupName}:</p>
                                        {addItems.map((add, idx) => {
                                          // Usar preço original se disponível (para adicionais incluídos no preço base)
                                          const displayPrice = add.original_price || add.price || 0;
                                          const isIncludedInBase = add.included_in_base_price || add.price === 0;
                                          
                                          return (
                                            <p key={idx} className="text-[10px] sm:text-xs text-green-400 pl-2">
                                              + {add.name || add.additional_name}
                                              {displayPrice > 0 && (
                                                <span className={`ml-1 ${isIncludedInBase ? 'text-emerald-400' : 'text-yellow-400'}`}>
                                                  (R$ {Number(displayPrice).toFixed(2)})
                                                  {isIncludedInBase && <span className="text-[8px] text-gray-500 ml-1">✓ no preço</span>}
                                                </span>
                                              )}
                                            </p>
                                          );
                                        })}
                                      </div>
                                    ))}
                                    {/* Adicionais sem grupo */}
                                    {ungrouped.length > 0 && (
                                      <div>
                                        {ungrouped.map((add, idx) => {
                                          const displayPrice = add.original_price || add.price || 0;
                                          const isIncludedInBase = add.included_in_base_price || add.price === 0;
                                          
                                          return (
                                            <p key={idx} className="text-[10px] sm:text-xs text-green-400">
                                              + {add.name || add.additional_name}
                                              {displayPrice > 0 && (
                                                <span className={`ml-1 ${isIncludedInBase ? 'text-emerald-400' : 'text-yellow-400'}`}>
                                                  (R$ {Number(displayPrice).toFixed(2)})
                                                  {isIncludedInBase && <span className="text-[8px] text-gray-500 ml-1">✓ no preço</span>}
                                                </span>
                                              )}
                                            </p>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {item.notes && (
                            <p className="text-[10px] sm:text-xs text-yellow-400 mt-1 italic break-words">📝 {item.notes}</p>
                          )}
                        </div>
                        <p className="text-green-400 font-bold text-xs sm:text-base whitespace-nowrap flex-shrink-0">
                          R$ {(Number(item.total || item.price || 0) * (item.quantity || 1)).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Cupom de Desconto - se houver */}
              {selectedOrder.coupon_code && selectedOrder.coupon_discount > 0 && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🎟️</span>
                      <div>
                        <p className="text-emerald-400 text-xs sm:text-sm font-semibold">Cupom de Desconto</p>
                        <p className="text-emerald-300 text-xs font-medium">{selectedOrder.coupon_code}</p>
                      </div>
                    </div>
                    <p className="text-emerald-400 font-bold text-sm sm:text-lg">
                      -R$ {Number(selectedOrder.coupon_discount).toFixed(2)}
                    </p>
                  </div>
                </div>
              )}

              {/* Total - Destacado */}
              <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500/40 rounded-lg p-3 sm:p-4">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-green-300 text-xs sm:text-sm font-semibold">Total do Pedido</p>
                    {selectedOrder.delivery_fee > 0 && (
                      <p className="text-gray-400 text-[10px] sm:text-xs">
                        (inclui taxa de R$ {Number(selectedOrder.delivery_fee).toFixed(2)})
                      </p>
                    )}
                  </div>
                  <p className="text-2xl sm:text-3xl font-bold text-green-400">
                    R$ {Number(selectedOrder.total || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Observações */}
              {selectedOrder.notes && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 sm:p-4">
                  <div className="flex items-start gap-2">
                    <FileText className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] sm:text-xs text-yellow-400 font-semibold uppercase">Observações</p>
                      <p className="text-white text-xs sm:text-sm mt-1">{selectedOrder.notes}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Motivo da Rejeição (se rejeitado) */}
              {selectedOrder.status === 'rejeitado' && selectedOrder.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 sm:p-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[10px] sm:text-xs text-red-400 font-semibold uppercase">Motivo da Rejeição</p>
                      <p className="text-white text-xs sm:text-sm mt-1">{selectedOrder.rejection_reason}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Botões de Ação - Fixo no mobile */}
              <div className="sticky bottom-0 left-0 right-0 bg-gray-900/95 backdrop-blur-sm p-3 -mx-3 sm:mx-0 sm:p-0 sm:bg-transparent sm:relative border-t border-gray-700 sm:border-0 mt-2 sm:mt-4">
                {/* Linha principal de ações */}
                <div className="flex gap-2 sm:gap-3">
                  <button
                    onClick={() => printOrder(selectedOrder)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-xs sm:text-sm font-semibold transition-all"
                    data-testid="modal-print-order-btn"
                  >
                    <Printer className="w-4 h-4" />
                    <span className="hidden sm:inline">Imprimir</span>
                  </button>
                  
                  {/* Botão Preparar/Próximo status - apenas para pedidos ativos */}
                  {(selectedOrder.status === 'pendente' || selectedOrder.status === 'em_producao') && (
                    <button
                      onClick={() => {
                        updateOrderStatus(selectedOrder.id, getNextStatus(selectedOrder.status));
                        setShowOrderDetails(false);
                      }}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white rounded-lg text-xs sm:text-sm font-bold shadow-lg transition-all"
                      data-testid="modal-advance-status-btn"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {selectedOrder.status === 'pendente' ? 'Preparar' : getNextStatusLabel(selectedOrder.status, selectedOrder.delivery_type)}
                    </button>
                  )}
                </div>

                {/* Linha secundária: Rejeitar e Excluir */}
                <div className="flex gap-2 sm:gap-3 mt-2">
                  {/* Botão Rejeitar - apenas para pedidos pendentes ou em produção */}
                  {!['pronto', 'cancelado', 'rejeitado'].includes(selectedOrder.status) && (
                    <button
                      onClick={() => openRejectModal(selectedOrder)}
                      className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-400 hover:text-red-300 rounded-lg text-xs sm:text-sm font-semibold transition-all"
                      data-testid="modal-reject-order-btn"
                    >
                      <XCircle className="w-4 h-4" />
                      Rejeitar
                    </button>
                  )}

                  {/* Botão Excluir - disponível para todos os pedidos */}
                  <button
                    onClick={() => openDeleteModal(selectedOrder)}
                    className="flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 bg-gray-800 hover:bg-gray-700 border border-gray-600 text-gray-400 hover:text-gray-300 rounded-lg text-xs sm:text-sm font-semibold transition-all"
                    data-testid="modal-delete-order-btn"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span className="hidden sm:inline">Excluir</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDV Screen */}
      {isPDVOpen && (
        <PDVScreen onClose={() => {
          setIsPDVOpen(false);
          fetchOrders();
        }} />
      )}

      {/* Modal de Seleção de Entregador */}
      {showDriverModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-sm border border-purple-500/30 shadow-xl animate-in fade-in zoom-in duration-200">
            <div className="p-4 border-b border-gray-700 flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-full">
                <Bike className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Selecionar Entregador</h3>
                <p className="text-gray-400 text-xs">Pedido #{pendingDeliveryOrder?.order_number || pendingDeliveryOrder?.id?.slice(0, 8)}</p>
              </div>
            </div>
            
            <div className="p-4 space-y-2 max-h-64 overflow-y-auto">
              {deliveryDrivers.map((driver) => (
                <button
                  key={driver.id}
                  onClick={() => handleSelectDriver(driver)}
                  disabled={notifyingDriver}
                  className="w-full p-3 bg-gray-700/50 hover:bg-purple-500/20 border border-gray-600 hover:border-purple-500/50 rounded-lg flex items-center gap-3 transition-all disabled:opacity-50"
                  data-testid={`select-driver-${driver.id}`}
                >
                  <div className="p-2 bg-purple-500/20 rounded-full">
                    <User className="w-4 h-4 text-purple-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-white font-medium">{driver.name}</p>
                    <p className="text-gray-400 text-xs flex items-center gap-1">
                      <Phone className="w-3 h-3" />
                      {driver.phone?.replace(/(\d{2})(\d{2})(\d{5})(\d{4})/, '($2) $3-$4')}
                    </p>
                  </div>
                  {notifyingDriver && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                  )}
                </button>
              ))}
            </div>
            
            <div className="p-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowDriverModal(false);
                  setPendingDeliveryOrder(null);
                }}
                className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-all"
              >
                Cancelar (não notificar)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Rejeição de Pedido */}
      {showRejectModal && rejectingOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
          <div className="bg-gray-800 rounded-2xl w-full max-w-md border border-red-500/30 shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-500/20 rounded-xl">
                  <XCircle className="w-6 h-6 text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Rejeitar Pedido</h3>
                  <p className="text-sm text-gray-400">#{rejectingOrder.order_number}</p>
                </div>
              </div>
              <p className="text-sm text-gray-300 mt-3">
                Selecione o motivo da rejeição. O cliente será notificado via WhatsApp.
              </p>
            </div>

            {/* Motivos de Rejeição */}
            <div className="p-4 max-h-80 overflow-y-auto">
              <div className="grid grid-cols-2 gap-2">
                {REJECT_REASONS.map((reason) => (
                  <button
                    key={reason.id}
                    onClick={() => setSelectedRejectReason(reason.id)}
                    className={`p-3 rounded-xl border-2 transition-all text-left ${
                      selectedRejectReason === reason.id
                        ? 'bg-red-500/20 border-red-500 text-white'
                        : 'bg-gray-700/50 border-gray-600 text-gray-300 hover:border-gray-500 hover:bg-gray-700'
                    }`}
                    data-testid={`reject-reason-${reason.id}`}
                  >
                    <span className="text-2xl block mb-1">{reason.icon}</span>
                    <span className="text-sm font-medium">{reason.label}</span>
                  </button>
                ))}
              </div>

              {/* Campo de motivo personalizado */}
              {selectedRejectReason === 'outro' && (
                <div className="mt-4 animate-in slide-in-from-top duration-200">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Descreva o motivo:
                  </label>
                  <textarea
                    value={customRejectMessage}
                    onChange={(e) => setCustomRejectMessage(e.target.value)}
                    placeholder="Digite aqui o motivo da rejeição..."
                    className="w-full p-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-red-500 focus:ring-1 focus:ring-red-500 resize-none"
                    rows={3}
                    data-testid="reject-custom-message"
                  />
                </div>
              )}
            </div>

            {/* Footer com botões */}
            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectingOrder(null);
                  setSelectedRejectReason('');
                  setCustomRejectMessage('');
                }}
                disabled={isRejecting}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                data-testid="reject-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={confirmRejectOrder}
                disabled={isRejecting || !selectedRejectReason || (selectedRejectReason === 'outro' && !customRejectMessage.trim())}
                className="flex-1 px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="reject-confirm-btn"
              >
                {isRejecting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Rejeitando...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Confirmar Rejeição
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Exclusão de Pedido */}
      {showDeleteModal && deletingOrder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4 animate-in fade-in duration-200">
          <div className="bg-gray-800 rounded-2xl w-full max-w-sm border border-gray-600 shadow-2xl animate-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-5 text-center">
              <div className="w-16 h-16 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Excluir Pedido</h3>
              <p className="text-sm text-gray-400">#{deletingOrder.order_number}</p>
            </div>

            {/* Conteúdo */}
            <div className="px-5 pb-4">
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <p className="text-sm text-yellow-200 text-center">
                  <strong>Atenção:</strong> O pedido será removido da sua lista de pedidos. 
                  O cliente <strong>não será notificado</strong> sobre esta exclusão.
                </p>
              </div>
            </div>

            {/* Footer com botões */}
            <div className="p-4 border-t border-gray-700 flex gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingOrder(null);
                }}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-xl transition-all disabled:opacity-50"
                data-testid="delete-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDeleteOrder}
                disabled={isDeleting}
                className="flex-1 px-4 py-3 bg-gray-600 hover:bg-gray-500 text-white font-bold rounded-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="delete-confirm-btn"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Excluindo...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Excluir
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Wrapper com Error Boundary para capturar erros de renderização
const OrdersContentWithErrorBoundary = (props) => (
  <OrdersErrorBoundary>
    <OrdersContent {...props} />
  </OrdersErrorBoundary>
);

export default OrdersContentWithErrorBoundary;
